import { createContext, useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import * as authApi from '../api/auth';
import { STORAGE_KEYS } from '../utils/constants';
import toast from 'react-hot-toast';

export const AuthContext = createContext(null);

const decodeBase64Url = (value) => {
  const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
  const padding = (4 - (normalized.length % 4)) % 4;
  const binary = atob(normalized + '='.repeat(padding));
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
  return new TextDecoder().decode(bytes);
};

const decodeToken = (token) => {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = JSON.parse(decodeBase64Url(parts[1]));
    return payload && typeof payload === 'object' ? payload : null;
  } catch (error) {
    console.error('Error decoding token:', error);
    return null;
  }
};

const isTokenExpired = (token) => {
  if (!token) return true;
  const payload = decodeToken(token);
  if (!payload || !payload.exp) return true;
  const expirationTime = payload.exp * 1000;
  return Date.now() >= expirationTime;
};

const getTimeUntilExpiry = (token) => {
  if (!token) return 0;
  const payload = decodeToken(token);
  if (!payload || !payload.exp) return 0;
  return Math.max(0, payload.exp * 1000 - Date.now());
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const expiryTimeoutRef = useRef(null);
  const navigate = useNavigate();

  const clearAuth = useCallback(() => {
    setUser(null);
    setToken(null);
    setIsAuthenticated(false);
    localStorage.removeItem(STORAGE_KEYS.TOKEN);
    localStorage.removeItem(STORAGE_KEYS.USER);
    if (expiryTimeoutRef.current) {
      clearTimeout(expiryTimeoutRef.current);
      expiryTimeoutRef.current = null;
    }
  }, []);

  const setupExpiryTimeout = useCallback((authToken) => {
    if (expiryTimeoutRef.current) {
      clearTimeout(expiryTimeoutRef.current);
      expiryTimeoutRef.current = null;
    }
    if (!authToken) return;
    const timeUntilExpiry = getTimeUntilExpiry(authToken);
    if (timeUntilExpiry <= 0) {
      clearAuth();
      toast.error('Your session has expired. Please log in again.');
      navigate('/login', { replace: true });
      return;
    }
    expiryTimeoutRef.current = setTimeout(() => {
      clearAuth();
      toast.error('Your session has expired. Please log in again.');
      navigate('/login', { replace: true });
    }, Math.max(timeUntilExpiry - 30000, 0));
  }, [clearAuth, navigate]);

  useEffect(() => {
    const initAuth = async () => {
      try {
        const storedToken = localStorage.getItem(STORAGE_KEYS.TOKEN);
        const storedUser = localStorage.getItem(STORAGE_KEYS.USER);

        if (storedToken && storedUser) {
          if (isTokenExpired(storedToken)) {
            localStorage.removeItem(STORAGE_KEYS.TOKEN);
            localStorage.removeItem(STORAGE_KEYS.USER);
            setLoading(false);
            return;
          }
          setToken(storedToken);
          setUser(JSON.parse(storedUser));
          setIsAuthenticated(true);
          setupExpiryTimeout(storedToken);
          try {
            const response = await authApi.getMe();
            setUser(response.payload.user);
            localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(response.payload.user));
          } catch {
            clearAuth();
          }
        }
      } catch {
        clearAuth();
      } finally {
        setLoading(false);
      }
    };
    initAuth();
    return () => {
      if (expiryTimeoutRef.current) clearTimeout(expiryTimeoutRef.current);
    };
  }, [setupExpiryTimeout, clearAuth]);

  const login = useCallback(async (credentials) => {
    try {
      const response = await authApi.login(credentials);
      const { user: userData, token: authToken } = response.payload;
      setUser(userData);
      setToken(authToken);
      setIsAuthenticated(true);
      localStorage.setItem(STORAGE_KEYS.TOKEN, authToken);
      localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(userData));
      setupExpiryTimeout(authToken);
      setTimeout(() => navigate('/dashboard', { replace: true }), 100);
      return { success: true, user: userData };
    } catch (error) {
      const message = error.response?.data?.message || 'Login failed';
      return { success: false, error: message };
    }
  }, [setupExpiryTimeout, navigate]);

  const register = useCallback(async (userData) => {
    try {
      const response = await authApi.register(userData);
      const { user: newUser, token: authToken } = response.payload;
      setUser(newUser);
      setToken(authToken);
      setIsAuthenticated(true);
      localStorage.setItem(STORAGE_KEYS.TOKEN, authToken);
      localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(newUser));
      setupExpiryTimeout(authToken);
      setTimeout(() => navigate('/dashboard', { replace: true }), 100);
      return { success: true, user: newUser };
    } catch (error) {
      const message = error.response?.data?.message || 'Registration failed';
      return { success: false, error: message };
    }
  }, [setupExpiryTimeout, navigate]);

  const logout = useCallback(async () => {
    try {
      if (token) await authApi.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      clearAuth();
      navigate('/login', { replace: true });
    }
  }, [token, clearAuth, navigate]);

  const updateUser = useCallback((updatedUser) => {
    setUser(updatedUser);
    localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(updatedUser));
  }, []);

  const value = {
    user,
    token,
    loading,
    isAuthenticated,
    login,
    register,
    logout,
    updateUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
